import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { DOCUMENT, inject, PLATFORM_ID } from '@angular/core';

import { catchError, map, Observable, of, tap } from 'rxjs';

import { CookieManagerResponse, Status } from './cookie-manager.interface';

export abstract class CookieBaseService {
	protected readonly platformId = inject(PLATFORM_ID);
	protected readonly document = inject<Document>(DOCUMENT);
	protected readonly http = inject(HttpClient);

	protected abstract getCookieServiceUrl(): string;

	setCookie(
		cookieName: string,
		cookieValue: Status,
	): Observable<CookieManagerResponse> {
		if (isPlatformBrowser(this.platformId)) {
			const url = new URL(this.getCookieServiceUrl());
			url.searchParams.append('setCookieName', cookieName);
			url.searchParams.append('setCookieValue', cookieValue);
			this.setLocalCookie(cookieName, cookieValue);

			return this.http.jsonp<CookieManagerResponse>(
				url.toString(),
				'callback',
			);
		}
		return of({ succes: true, cookiestatus: Status.ACCEPTED });
	}

	getCookie(cookieName: string): Observable<Status> {
		if (isPlatformBrowser(this.platformId)) {
			const localValue = this.getLocalCookieValue(cookieName);
			if (localValue !== Status.EMPTY) {
				return of(localValue);
			}

			const url = new URL(this.getCookieServiceUrl());
			url.searchParams.append('getCookie', cookieName);
			return this.http
				.jsonp<CookieManagerResponse>(url.toString(), 'callback')
				.pipe(
					map((response) => ({
						...response,
						cookiestatus:
							response.cookiestatus.toLowerCase() as Status,
					})),
					tap((response) => {
						if (response.cookiestatus !== Status.EMPTY) {
							this.setLocalCookie(
								cookieName,
								response.cookiestatus,
							);
						}
					}),
					map((response) => response.cookiestatus),
					catchError(() => of(Status.EMPTY)),
				);
		}
		return of(Status.ACCEPTED);
	}

	getLocalCookie(name: string): boolean {
		if (isPlatformBrowser(this.platformId)) {
			const regex = new RegExp(`(^| )${name}=([^;]+)`);
			const match = regex.exec(this.document.cookie);
			return !!match;
		}
		return false;
	}

	getLocalCookieValue(name: string): Status {
		if (isPlatformBrowser(this.platformId)) {
			const regex = new RegExp(`(^| )${name}=([^;]+)`);
			const match = regex.exec(this.document.cookie);
			if (match) {
				const value = match[2];
				if (value === Status.ACCEPTED) return Status.ACCEPTED;
				if (value === Status.DECLINED) return Status.DECLINED;
			}
		}
		return Status.EMPTY;
	}

	getLocalCookieValueAsBoolean(name: string): boolean {
		if (isPlatformBrowser(this.platformId)) {
			const regex = new RegExp(`(^| )${name}=([^;]+)`);
			const match = regex.exec(this.document.cookie);
			if (match) return match[2] === Status.ACCEPTED;
		}
		return false;
	}

	/* v8 ignore start */
	private setLocalCookie(cookieName: string, cookieValue: Status): void {
		if (isPlatformBrowser(this.platformId)) {
			const expires = new Date();
			expires.setFullYear(expires.getFullYear() + 1);
			this.document.cookie = `${cookieName}=${cookieValue}; Expires=${expires.toUTCString()}; Path=/; SameSite=Lax`;
		}
	}
	/* v8 ignore stop */
}
